import React, { useEffect, useState } from 'react'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material'
import Tooltip from '@mui/material/Tooltip'
import { t } from '../../i18n'
import Icon from '../shared/icon'

interface OrderProvider {
  id?: number
  provider_type: string
  credentials: Record<string, string>
  connected: boolean
  destroyable: boolean
  robo_api_key_set: boolean
  _destroy?: boolean
}

export interface CredentialField {
  name: string
  sensitive: boolean
}

interface OrderProviderFormProps {
  orderProvider: OrderProvider
  requiredFields: CredentialField[]
  isSubmitted: boolean
  connectError: string
  connectOrderProvider: (orderProviderId: number, setIsConnecting: React.Dispatch<React.SetStateAction<boolean>>) => void
  setIsSubmitted: (isSubmitted: boolean) => void
  onSubmit: (data: OrderProvider) => void
}

const OrderProviderForm: React.FC<OrderProviderFormProps> = ({
  orderProvider,
  requiredFields,
  onSubmit,
  connectOrderProvider,
  connectError,
  isSubmitted,
  setIsSubmitted
}) => {

  const [isConnecting, setIsConnecting] = useState(false)

  const validationSchema = Yup.object(
    requiredFields.reduce((schema, { name }) => {
      schema[name] = Yup.string().required(t(`order_providers.${name}_required`))
      return schema
    }, {} as Record<string, Yup.StringSchema>)
  )

  const initialValues = requiredFields.reduce((values, { name }) => {
    values[name] = orderProvider.credentials[name] ?? ''
    return values
  }, {} as Record<string, string>)


  const canConnect = () => {
    return !!orderProvider.id && !orderProvider.connected
  }

  const onConnectButtonClick = (e) => {
    e.preventDefault()
    e.stopPropagation() // prevent accordion from closing
    setIsConnecting(true)
    connectOrderProvider(orderProvider.id, setIsConnecting)
  }

  const renderConnectIcon = () => {
    if (orderProvider.connected) {
      // already connected, render connected
      return renderConnectedIcon()
    } else if (canConnect()) {
      // not connected, but can connect
      return <>
        <Tooltip
          title={'Provider not connected'}
          placement='top'
          arrow
        >
          <i className="fa fa-exclamation-triangle ms-2" style={{ color: 'orange' }} />
        </Tooltip>
        <button
          type="button"
          className="btn btn-secondary btn-connect"
          style={{ marginLeft: '10px' }}
          disabled={isConnecting}
          onClick={onConnectButtonClick}
          >
            {isConnecting ? 'Connecting...' : 'Connect'}
        </button>
      </>
    }
    // not connected and cannot connect yet
    return null
  }

  const renderConnectedIcon = () => {
    if (!orderProvider.connected) return null
    return (
      <Tooltip
        title={'Provider connected'}
        placement='top'
        arrow
      >
        <i className="fa fa-link ms-2"></i>
      </Tooltip>
    )
  }

  const renderSubmittedIcon = () => {
    if(!isSubmitted) {
      return null
    }

    return (
      <Icon className="order-provider-valid-icon me-2" name="success" />
    )
  }

  return (
    <Accordion defaultExpanded className="mb-4">
      <AccordionSummary expandIcon={<Icon name="arrowDown" color="white" />}>
        <h5 style={{ marginBottom: 0 }}>
          {t('order_providers.set_up', { provider_type: orderProvider.provider_type })}
          {orderProvider.provider_type == 'SIMPHONY' && renderConnectIcon()}
        </h5>
      </AccordionSummary>
      <AccordionDetails>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize={true}
          validateOnChange={true}
          validateOnBlur={true}
          onSubmit={(values, { setSubmitting }) => {
            const updatedProvider = {
              ...orderProvider,
              credentials: values
            }
            onSubmit(updatedProvider)
            setIsSubmitted(true)
            setSubmitting(false)
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting
          }) => {

            useEffect(() => {
              if (Object.keys(errors).length > 0 && isSubmitted) {
                setIsSubmitted(false)
              }
            }, [errors, isSubmitted])

            return (
              <div>
                {requiredFields.map(({ name, sensitive }) => (
                  <div className="form-group" key={name}>
                    <label htmlFor={name}>{t(`order_providers.${name}`)}</label>
                    <input
                      id={name}
                      name={name}
                      type={sensitive ? 'password' : 'text'}
                      autoComplete={sensitive ? 'new-password' : 'off'}
                      value={values[name]}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`form-control ${
                        errors[name] && touched[name] ? 'is-invalid' : ''
                      }`}
                    />
                    {errors[name] && touched[name] && (
                      <div className="invalid-feedback">{errors[name]}</div>
                    )}
                  </div>
                ))}

                {(orderProvider.provider_type == 'INFOR' && !!orderProvider.id) &&
                  <div className="form-group ms-2" key={'robo_api_key_set'}>
                    <div className="d-flex align-items-center">
                      <div className="d-flex align-items-center">
                        {orderProvider.robo_api_key_set ? (
                          <i className="fa fa-check-circle me-2" style={{ color: '#78B730', fontSize: '16px' }} />
                        ) : (
                          <i className="fa fa-times-circle me-2" style={{ color: '#A6A6A6', fontSize: '16px' }} />
                        )}
                        <label className="form-check-label ms-0" style={{marginTop: '5px' }}>
                          {
                          !!orderProvider.robo_api_key_set
                            ? t('order_providers.robo_api_key_configured')
                            : t('order_providers.robo_api_key_not_configured')
                          }
                        </label>
                      </div>
                    </div>
                  </div>
                }

                <div className="form-actions d-flex justify-content-end">
                  {
                    connectError &&
                    <div className="me-3" style={{display: 'flex', alignItems: 'center'}}>
                      <Icon className="order-provider-error-icon me-2" name="error" />
                      <span style={{color: '#D0021B'}}>Connection request failed - {connectError}</span>
                    </div>
                  }
                  {
                    orderProvider.provider_type == 'SIMPHONY' && isSubmitted && !connectError &&
                    <div className="me-3" style={{display: 'flex', alignItems: 'center'}}>
                      {renderSubmittedIcon()} Form submitted
                    </div>
                  }
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => handleSubmit()}
                    disabled={isSubmitting}
                  >
                    Submit
                  </button>
                </div>
              </div>
            )
          }}
        </Formik>
      </AccordionDetails>
    </Accordion>
  )
}

export default OrderProviderForm
